class DeliveryTimer extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null; // Store the interval ID for updating the timer
    this.timerDisplay = null; // Store the timer display element
    this.pageVisible = true; // Track whether the page is currently visible
  }

  async connectedCallback() {
    // Listen for visibility change events
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    this.initializeTimer();
  }

  initializeTimer() {
    // Fetch the GeoIP data using your script and wait for the response
    let deliveryTimerElement = this;

    fetch(
      window.Shopify.routes.root +
        'browsing_context_suggestions.json' +
        '?country[enabled]=true' +
        `&country[exclude]=${window.Shopify.country}` +
        '&language[enabled]=true' +
        `&language[exclude]=${window.Shopify.language}`
    )
      .then((response) => response.json())
      .then((geoIpData) => {
        // Check if the page is currently visible before proceeding
        if (!this.pageVisible) {
          return;
        }

        // Retrieve the JSON data from the embedded script tag
        const deliveryTimersScript = this.querySelector(
          'script[type="application/json"]'
        );
        if (!deliveryTimersScript) {
          console.error('No JSON data found.');
          return;
        }

        // Parse the JSON data
        const deliveryTimersData = JSON.parse(
          deliveryTimersScript.textContent
        );

        // Set the current date for testing (Friday)
        const currentDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000); // new Date('2023/12/8 13:59:50');

        // Define the current day based on the currentDate
        const daysOfWeek = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
        const currentDayIndex = currentDate.getDay();
        const currentDay = daysOfWeek[currentDayIndex];

        // Check if any of the delivery timers match the active days and location
        const matchingTimers = deliveryTimersData.deliveryTimers.filter(
          (timer) => {
            return (
              timer.active_days.includes(currentDay) &&
              this.shouldShowTimer(geoIpData, timer)
            );
          }
        );

        if (matchingTimers.length > 0) {
          // For this example, let's assume you want to display the first matching timer
          const firstMatchingTimer = matchingTimers[0];

          // Get the target order time (e.g., "14:00")
          const orderTimeParts = firstMatchingTimer.order_by_time.split(
            ':'
          );
          const targetHour = parseInt(orderTimeParts[0]);
          const targetMinute = parseInt(orderTimeParts[1]);

          // Calculate the remaining time in seconds until the target order time on Monday
          let timeRemainingInSeconds = this.calculateTimeRemaining(
            targetHour,
            targetMinute,
            currentDate
          );

          // Create a new div element to display the timer text
          const newTimerDisplay = document.createElement('div');

          // Function to update and display the timer and text
          const updateTimerDisplay = () => {
            if (!this.pageVisible) {
              return; // Don't update the timer if the page is not visible
            }

            const hours = Math.floor(timeRemainingInSeconds / 3600);
            const minutes = Math.floor(
              (timeRemainingInSeconds % 3600) / 60
            );
            const seconds = Math.floor(timeRemainingInSeconds % 60);

            // Format the remaining time as "2h 46m 43s"
            let formattedTime = '';
            if (hours > 0) {
              formattedTime += `${hours}h `;
            }
            if (minutes > 0) {
              formattedTime += `${minutes}m `;
            }

            if (seconds > 0) {
              formattedTime += `${seconds}s`;
            }

            // Calculate the delivery day based on the duration in hours
            const durationParts = firstMatchingTimer.duration.match(
              /\d+/
            ); // Extract the numeric part (e.g., "72")
            const durationInHours = parseInt(durationParts[0]);
            let deliveryDate = new Date(
              currentDate.getTime() + durationInHours * 3600 * 1000
            );
            // check if the current timer has passed the order by time if it has add 24hrs to the delivery date
            // do the check by comparing the current time to the order by time
            const orderTime = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
              targetHour,
              targetMinute
            );
            if (currentDate > orderTime) {
              deliveryDate = new Date(
                deliveryDate.getTime() + 24 * 3600 * 1000
              );
            }
            const deliveryDayIndex = deliveryDate.getDay();
            const deliveryDay = daysOfWeek[deliveryDayIndex];
            // Fri, Sep 09
            let formattedDeliveryDate = deliveryDate.toLocaleDateString(
              'en-US',
              {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              }
            );

            // Replace both placeholders with actual values

            const timerText = firstMatchingTimer.order_by_text
              .replace('{TIMER}', formattedTime)
              .replace('{DELIVERY-DAY}', deliveryDay)
              .replace('{DELIVERY-DATE}', formattedDeliveryDate);

            // Set the innerHTML of the created div element to update the timer display
            newTimerDisplay.innerHTML = timerText;

            // Update the time remaining
            timeRemainingInSeconds--;

            // When the time reaches zero, reinitialize the timer
            if (timeRemainingInSeconds <= 0) {
              clearInterval(this.timerInterval);
              this.initializeTimer();
            }
          };

          // Call the updateTimerDisplay function immediately
          updateTimerDisplay();

          // Update custom elements style color and background color
          if (!firstMatchingTimer.order_before_text_color) {
            firstMatchingTimer.order_before_text_color = '';
          }
          if (!firstMatchingTimer.order_before_background_color) {
            firstMatchingTimer.order_before_background_color = '';
          }
          this.style.color = firstMatchingTimer.order_before_text_color;
          this.style.backgroundColor = firstMatchingTimer.order_before_background_color;

          if (this.timerDisplay) {
            // If a timer display already exists, replace it
            this.replaceChild(newTimerDisplay, this.timerDisplay);
          } else {
            // Append the created div element as a child of the custom element
            this.appendChild(newTimerDisplay);
          }

          // Store the new timer display
          this.timerDisplay = newTimerDisplay;

          // Set an interval to update the timer display every second
          this.timerInterval = setInterval(updateTimerDisplay, 1000);
        }
      });
  }

  handleVisibilityChange() {
    // Update the pageVisible property when the page/tab visibility changes
    this.pageVisible = !document.hidden;
  }

  disconnectedCallback() {
    // Clean up the interval and remove event listener when the element is removed from the DOM
    clearInterval(this.timerInterval);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  calculateTimeRemaining(targetHour, targetMinute, currentDate) {
    const currentHour = currentDate.getHours();
    const currentMinute = currentDate.getMinutes();
    const currentSecond = currentDate.getSeconds();

    // Calculate the time remaining in seconds until the target time on the same day
    let timeRemainingInSeconds =
      (targetHour - currentHour) * 3600 +
      (targetMinute - currentMinute) * 60 -
      currentSecond;

    // Ensure the time remains positive (e.g., if the target time is in the past, set it to 24 hours)
    if (timeRemainingInSeconds < 0) {
      timeRemainingInSeconds += 24 * 3600; // 24 hours in seconds
    }

    return timeRemainingInSeconds;
  }

  shouldShowTimer(geoIpData, timer) {
    const detectedCountry = geoIpData.detected_values.country.name;
    return (
      timer.location.includes(detectedCountry) ||
      timer.location.includes('All locations')
    );
  }
}

window.customElements.define('delivery-timer', DeliveryTimer);
